import { useAutoneTranslation } from '@autone/translations';
import {
  forwardRef,
  type HTMLAttributes,
  type MouseEvent,
  type PropsWithChildren,
  type PropsWithRef,
  type ReactNode,
  useEffect,
  useImperativeHandle,
  useReducer,
  useRef,
} from 'react';

import { type ButtonProps, cn, TailwindButton } from '../../../../tailwind';

import {
  AdvancedFiltersContext,
  useAdvancedFiltersContext,
} from './AdvancedFilters.context';
import { advancedFiltersReducer } from './AdvancedFilters.reducer';
import { AdvancedFiltersConditionCombineMode } from './components/AdvancedFiltersConditionCombineMode';
import { AdvancedFiltersConditionRow } from './components/AdvancedFiltersConditionRow';
import {
  type AdvancedFilterCondition,
  type AdvancedFiltersConfig,
  type InferAdvancedFilterPublicState,
} from './types/config.types';
import { type AdvancedFiltersPublicState } from './types/state.types';
import {
  advancedFilterConditionIsComplete,
  initialiseAdvancedFiltersReducer,
} from './utilities/reducer-utils';

export interface AdvancedFiltersContextProviderRef<
  U extends AdvancedFilterCondition = AdvancedFilterCondition,
> {
  /**
   * Call this method to flush new advanced filters into the context state
   * without remounting the component.
   */
  flushNewAdvancedFilters: (
    advancedFilters: AdvancedFiltersPublicState<U>,
  ) => void;
  /**
   * Call this method to remove all incomplete conditions from the context
   * state without remounting the component.
   */
  removeIncompleteRows: () => void;
}

interface AdvancedFiltersContextProps<
  Config extends AdvancedFiltersConfig = AdvancedFiltersConfig,
> extends PropsWithChildren {
  advancedFiltersConfig: Config;
  defaultAdvancedFilters?: InferAdvancedFilterPublicState<Config>;
  onAdvancedFiltersChange?: (
    newAdvancedFilters: InferAdvancedFilterPublicState<Config>,
  ) => void;
}

const _AdvancedFiltersContextProvider = forwardRef<
  AdvancedFiltersContextProviderRef,
  AdvancedFiltersContextProps
>(
  (
    {
      advancedFiltersConfig,
      defaultAdvancedFilters,
      onAdvancedFiltersChange,
      children,
    },
    ref,
  ) => {
    const prevCompletedState = useRef<string | null>(null);
    const [state, dispatch] = useReducer(
      advancedFiltersReducer,
      defaultAdvancedFilters,
      initialiseAdvancedFiltersReducer,
    );

    // expose imperative methods to parent component
    useImperativeHandle(ref, () => ({
      flushNewAdvancedFilters: (newAdvancedFilters) => {
        dispatch({
          type: 'FLUSH_NEW_ADVANCED_FILTERS',
          payload: newAdvancedFilters,
        });
      },
      removeIncompleteRows: () => {
        dispatch({ type: 'REMOVE_INCOMPLETE_ROWS' });
      },
    }));

    useEffect(() => {
      // only call `onAdvancedFiltersChange` with completed conditions
      const completedState = {
        conditionCombineMode: state.conditionCombineMode,
        conditions: Object.values(state.conditions).filter((condition) =>
          advancedFilterConditionIsComplete(condition),
        ),
      };
      const completedStateString = JSON.stringify(completedState);

      if (
        prevCompletedState.current &&
        prevCompletedState.current !== completedStateString
      ) {
        onAdvancedFiltersChange?.(completedState);
      }

      prevCompletedState.current = completedStateString;
    }, [state, onAdvancedFiltersChange]);

    return (
      <AdvancedFiltersContext.Provider
        value={{ state, dispatch, config: advancedFiltersConfig }}
      >
        {children}
      </AdvancedFiltersContext.Provider>
    );
  },
);

export const AdvancedFiltersContextProvider =
  _AdvancedFiltersContextProvider as <Config extends AdvancedFiltersConfig>(
    props: PropsWithRef<AdvancedFiltersContextProps<Config>>,
  ) => ReactNode;

type AdvancedFiltersContentProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  'children'
>;

export const AdvancedFiltersContent = forwardRef<
  HTMLDivElement,
  AdvancedFiltersContentProps
>(({ className, ...props }, ref) => {
  const { common } = useAutoneTranslation();
  const { state, dispatch } = useAdvancedFiltersContext();
  const { conditions } = state;

  return (
    <div
      className={cn(
        'flex flex-col grow p-4 overflow-y-scroll no-scrollbar',
        className,
      )}
      {...props}
      ref={ref}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3">
          {Object.keys(conditions).map((rowId, idx) => (
            <div
              key={rowId}
              className="flex flex-row gap-2 items-center w-full"
            >
              <div className="basis-1/6 italic">
                <AdvancedFiltersConditionCombineMode rowIdx={idx} />
              </div>
              <AdvancedFiltersConditionRow rowId={rowId} />
            </div>
          ))}
        </div>
        <TailwindButton
          size={'md'}
          variant={'ghost'}
          className="w-fit"
          onClick={() => dispatch({ type: 'ADD_CONDITION' })}
        >
          {common('advanced-filters.add-row')}
        </TailwindButton>
      </div>
    </div>
  );
});

export const AdvancedFiltersClearAllButton = forwardRef<
  HTMLButtonElement,
  ButtonProps
>(({ onClick, ...props }, ref) => {
  const { dispatch } = useAdvancedFiltersContext();

  const handleOnClick = (e: MouseEvent<HTMLButtonElement>) => {
    dispatch({ type: 'RESET' });
    onClick?.(e);
  };

  return (
    <TailwindButton
      onClick={handleOnClick}
      variant={'outlined'}
      color={'secondary'}
      {...props}
      ref={ref}
    />
  );
});
